import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { LoggerFactory } from '@frontend/sports/common/core/feature/logging';
import { DeviceService, NativeAppService, WINDOW } from '@frontend/vanilla/core';
import type JSPDF from 'jspdf';
import { BehaviorSubject, Observable } from 'rxjs';

import { PrintErrorDialogService } from './print-error-dialog.service';
import { PrintLoadingIndicatorDialogService } from './print-loading-indicator-dialog.service';
import { PrintCallbackType, PrintDeviceType } from './print.models';

@Injectable({ providedIn: 'root' })
export class PrintService {
    private url$ = new BehaviorSubject<SafeUrl>('');
    private nativeAppService = inject(NativeAppService);
    private deviceService = inject(DeviceService);
    private logger = inject(LoggerFactory).getLogger('PrintService');

    private readonly _window = inject(WINDOW);
    private readonly _doc = inject(DOCUMENT);

    constructor(
        private sanitizer: DomSanitizer,
        private loadingIndicatorService: PrintLoadingIndicatorDialogService,
        private printErrorDialogService: PrintErrorDialogService,
    ) {
        this.setUrlEmpty();
    }

    print(url: string): void {
        this.loadingIndicatorService.show();
        this._window.addEventListener('message', this.handleIframeMessage);
        const printCommand = this.getPrintCommand();
        url = this.appendDeviceToUrl(url, printCommand);
        this.url$.next(this.sanitizer.bypassSecurityTrustResourceUrl(url));
    }

    private appendDeviceToUrl(url: string | undefined, deviceCommand: string): string {
        if (!url) {
            return '';
        }

        const queryParamPrefix = url.includes('?') ? '&' : '?';
        return `${url}${queryParamPrefix}device=${deviceCommand}`;
    }

    getPrintUrl(): Observable<SafeUrl> {
        return this.url$.asObservable();
    }

    private setUrlEmpty(): void {
        this.url$.next(this.sanitizer.bypassSecurityTrustResourceUrl('about:blank'));
    }

    private getPrintCommand(): PrintDeviceType {
        if (this.nativeAppService.isNative) {
            return this.deviceService.isAndroid ? PrintDeviceType.Android : PrintDeviceType.Ios;
        }

        return PrintDeviceType.Default;
    }

    private handleIframeMessage = async (event: any) => {
        if (typeof event.data !== 'undefined') {
            switch (event.data.type as PrintCallbackType) {
                case PrintCallbackType.DataLoadingError:
                    this.loadingIndicatorService.close();
                    this._window.removeEventListener('message', this.handleIframeMessage);
                    this.printErrorDialogService.show();
                    break;
                case PrintCallbackType.PrintStarted:
                    this.loadingIndicatorService.close();
                    this._window.removeEventListener('message', this.handleIframeMessage);
                    break;
                case PrintCallbackType.PrintFinished:
                    this.setUrlEmpty();
                    break;
                case PrintCallbackType.PrintReadyForWrapper:
                    this._window.removeEventListener('message', this.handleIframeMessage);
                    this.triggerNativePrint();
                    break;
            }
        }
    };

    private triggerNativePrint(): void {
        import('jspdf').then((jsPDF) => {
            const pdf = new jsPDF.default({
                putOnlyUsedFonts: true,
                floatPrecision: 'smart',
                compress: true,
            });
            const iframeElement = this._doc.querySelector('.print-container') as HTMLIFrameElement;
            const iframeDocument = iframeElement?.contentWindow?.document?.documentElement;
            if (!iframeDocument) {
                this.logger.error('print container not found');
                return;
            }
            try {
                pdf.html(iframeDocument, {
                    callback: (doc: JSPDF) => {
                        const pdfBinaryData = doc.output('datauristring');

                        if (this.deviceService.isAndroid) {
                            const printEventDataAndroid = {
                                eventName: 'PREVIEW_DOWNLOAD_PDF',
                                parameters: {
                                    data: pdfBinaryData.split(',')[1],
                                    filename: 'betslip',
                                },
                            };
                            this.nativeAppService.sendToNative(printEventDataAndroid);
                        } else if (this.deviceService.isiOS) {
                            const printEventDataIOS = {
                                eventName: 'PRINT_PDF',
                                parameters: {
                                    url: pdfBinaryData,
                                    filename: 'betslip',
                                },
                            };
                            this.nativeAppService.sendToNative(printEventDataIOS);
                        }
                        this.loadingIndicatorService.close();
                        this.setUrlEmpty();
                    },
                    x: 10,
                    y: 10,
                    width: 190,
                    windowWidth: 1000,
                });
            } catch (error) {
                this.logger.error(error, 'JsPDF generation failed');
                this.loadingIndicatorService.close();
                this.setUrlEmpty();
            }
        });
    }
}
